import { memo, useMemo } from "react";

import { FormState, SaveMode } from "@/utils/hooks/useSave";
import Valkyrie, { viCheck, viCircleExclamation, viFloppyDisk, viSpinnerThird } from "@sippy-platform/valkyrie";

import { Box, Button, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";

function SaveIndicator({ formState, disabled, save, mini = false, errors = {} }) {
  const status = useMemo(() => {
    if (formState.saveMode === SaveMode.MANUAL) {
      switch (formState.status) {
        case FormState.UNCHANGED:
        case FormState.SAVED:
          return {
            message: "Click to save changes.",
            color: "primary",
            label: "Save",
            icon: { icon: viFloppyDisk },
          };
        case FormState.AWAITING:
          return {
            message: "Awaiting your changes...",
            color: "primary",
            label: "Save",
            icon: { icon: viFloppyDisk },
          };
        case FormState.UNSAVED:
          return {
            message: "We'll save your changes shortly.",
            color: "primary",
            label: "Save",
            icon: { icon: viFloppyDisk },
          };
        case FormState.PENDING:
          return {
            message: "Saving your changes...",
            color: "primary",
            label: "Saving",
            icon: { icon: viSpinnerThird, spin: true },
          };
        case FormState.ERROR:
          return {
            message: "An unknown error occurred.",
            color: "error",
            label: "Failed",
            icon: { icon: viCircleExclamation },
          };
      }
    }

    switch (formState.status) {
      case FormState.UNCHANGED:
        return {
          message: "Everything is up-to-date.",
          color: "success",
          label: "Up-to-date",
          icon: { icon: viCheck },
        };
      case FormState.SAVED:
        return {
          message: "Everything is up-to-date.",
          color: "success",
          label: "Saved",
          icon: { icon: viCheck },
        };
      case FormState.AWAITING:
        return {
          message: "Awaiting your changes...",
          color: "primary",
          label: "Changes required",
          icon: { icon: viCircleExclamation },
        };
      case FormState.UNSAVED:
        return {
          message: "We'll save your changes shortly.",
          color: "primary",
          label: "Unsaved",
          icon: { icon: viCheck },
        };
      case FormState.PENDING:
        return {
          message: "Saving your changes...",
          color: "primary",
          label: "Saving",
          icon: { icon: viSpinnerThird, spin: true },
        };
      case FormState.ERROR:
        return {
          message: "An unknown error occurred.",
          color: "error",
          label: "Failed",
          icon: { icon: viCircleExclamation },
        };
    }
  }, [formState]);

  return (
    <Tooltip
      title={
        Object.keys(errors).length > 0 ? (
          <>
            <Typography>Solve these issues to save your changes:</Typography>
            <Box component="ul" sx={{ pl: 2.5, mb: 0, mt: 1 }}>
              {Object.keys(errors).map((key) =>
                errors[key].map((error, k) => (
                  <Typography key={k}>
                    <li>{error}</li>
                  </Typography>
                )),
              )}
            </Box>
          </>
        ) : (
          <Typography>{status.message}</Typography>
        )
      }
    >
      <Box
        component="span"
        sx={[
          {
            display: "inline-block",
          },
          !mini && {
            ["@container (max-width: 540px)"]: {
              "& > button:nth-of-type(2)": {
                display: "none",
              },
            },
            ["@container (min-width: 540px)"]: {
              "& > button:first-of-type": {
                display: "none",
              },
            },
          },
          mini && {
            "& > button:nth-of-type(2)": {
              display: "none",
            },
          },
        ]}
      >
        <Button
          variant={formState.saveMode === SaveMode.MANUAL ? "contained" : undefined}
          color={status.color}
          onClick={save}
          sx={{ fontSize: 16, px: 1.25 }}
          disabled={disabled}
        >
          <Valkyrie {...status.icon} />
        </Button>
        <Button
          variant={formState.saveMode === SaveMode.MANUAL ? "contained" : undefined}
          color={status.color}
          onClick={save}
          startIcon={<Valkyrie {...status.icon} />}
          disabled={disabled}
        >
          {status.label}
        </Button>
      </Box>
    </Tooltip>
  );
}

SaveIndicator.propTypes = {
  formState: PropTypes.object,
  errors: PropTypes.object,
  save: PropTypes.func,
  mini: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default memo(SaveIndicator);
